import React, { useState, useEffect, useRef } from 'react';
import { Redirect } from 'react-router-dom';
import Select from 'react-select';
import 'react-select/dist/react-select.css';
import Swal from 'sweetalert2';
import { FormattedMessage, useIntl } from 'react-intl';
import Loading from '../ui/Loading.js';
import * as Utils from '../../commons/utils/Utils';
import Security from '../../commons/security/Security.js';
import ConfigBusiness from '../../commons/config/ConfigBusiness.js';

/** El catálogo: una de las dos pantallas de entrada, y el path de la API que trae el ítem. */
const URL_CATALOGO = '/inventario/items';

/** La otra entrada: la grilla de Stock por Depósito, por su acción + (VEC-3313 Sc. 04). */
const URL_STOCK = '/inventario/stock';

/**
 * Select de ítems del catálogo (VEC-3314 Sc. 01). Devuelve {items: [{value,label}], hayMas}.
 *
 * `hayMas` se ignora a propósito: existía para un aviso de "acotá la búsqueda" que se sacó por
 * decisión de producto. Sigue viajando en la respuesta y no se borra del contrato para no romper
 * a otro consumidor.
 */
const URL_ITEMS_SELECT = '/inventario/items/select';

/**
 * Espera antes de consultar el select mientras se tipea.
 *
 * react-select v1 llama a `loadOptions` en CADA tecla y el catálogo de un tenant real son miles de
 * repuestos: sin esto, escribir "filtro" son seis consultas de las que cinco no le importan a nadie.
 */
const DEBOUNCE_BUSQUEDA_MS = 300;

/**
 * A dónde volver al guardar o cancelar. Se decide por `?origen=` contra una LISTA BLANCA, y nunca
 * redirigiendo a una URL que venga cruda en el query string: eso sería un open redirect. Sin un
 * `origen` reconocido se vuelve al catálogo, que es el comportamiento histórico.
 *
 * Los redirects de ERROR (ítem inexistente en `?item=`, fallo del fetch) NO usan esto a propósito:
 * si el ítem preseleccionado no existe, el catálogo es donde se elige uno.
 */
const urlDeVuelta = (search) => {
  if (search && /[?&]origen=stock(&|$)/.test(String(search))) return URL_STOCK;

  return URL_CATALOGO;
};

/**
 * Tope de envases por ingreso. Réplica del backend
 * (ItemStockValidation::TOPE_CANTIDAD_INGRESO) para avisar sin ir y volver al servidor; la
 * autoridad sigue siendo el backend, que valida igual y responde 400.
 */
const TOPE_CANTIDAD = 1000;

/**
 * Hoy en horario LOCAL, como 'YYYY-MM-DD'.
 *
 * No se usa `toISOString()`, que devuelve UTC: en UTC-3, después de las 21:00 daría el día
 * siguiente y el ingreso quedaría fechado mañana. La fecha del remito es la del usuario.
 *
 * El padding va a mano y no con `padStart` para no depender de polyfills en los navegadores viejos
 * que soporta este build (react-scripts 2.1.8).
 */
const hoyLocal = () => {
  const d = new Date();
  const pad = (n) => (n < 10 ? '0' + n : '' + n);

  return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
};

/** Lee `?item=` del query string sin URLSearchParams, que no está en los navegadores del target. */
const itemDeQueryString = (search) => {
  if (!search) return null;

  const match = String(search).match(/[?&]item=(\d+)/);

  return match ? Number(match[1]) : null;
};

const FORM_INIT = {
  almacen: '',
  cantidad: '',
  costo: '',
  fecha: '',
  motivo: '',
};

/**
 * VEC-3314 — formulario de ingreso de stock. Un ítem por vez.
 *
 * TRES ENTRADAS, y el ítem se comporta distinto según cuál:
 *
 *   - catálogo (InventarioItemsGridRow) y fila de la grilla de stock (InventarioStockGridRow):
 *     traen `?item=` y el ítem queda FIJO y de solo lectura. Son los caminos que ya existían y no
 *     cambian: el usuario ya eligió el ítem antes de llegar acá, y volver a ofrecerle un combo
 *     sería invitarlo a equivocarse.
 *   - el botón + de la grilla de stock: entra SIN `?item=` y el ítem es un select buscable. Es lo
 *     que pide el Sc. 01 —"inicia un nuevo ingreso … selecciona el depósito destino, el ítem"— y
 *     lo que antes no se podía: sin `?item=` esta pantalla rebotaba al catálogo.
 *
 * El select no trae el catálogo entero: busca contra /inventario/items/select, que acota por texto
 * y tiene tope propio.
 *
 * El ítem viaja por query string y no por la ruta porque es una PRESELECCIÓN, no la identidad de la
 * pantalla: el día que el formulario acepte N ítems por movimiento (el esquema ya lo soporta), la
 * ruta no cambia.
 */
const InventarioStockIngresoAbm = (props) => {
  const intl = useIntl();

  const [redirectTo, setRedirectTo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ ...FORM_INIT, fecha: hoyLocal() });
  const [almacenes, setAlmacenes] = useState([]);
  const [item, setItem] = useState(null);
  /** La opción elegida en el select. Solo se usa cuando NO viene `?item=`. */
  const [itemSeleccionado, setItemSeleccionado] = useState(null);

  const debounceRef = useRef(null);

  const itemId = itemDeQueryString(props.location && props.location.search);
  const urlVolver = urlDeVuelta(props.location && props.location.search);

  useEffect(() => {
    if (!Security.hasPermission('INVENTARIO_STOCK_INGRESAR') || ConfigBusiness.get('inventarioItems.habilitado') !== 'true') {
      setRedirectTo('/error');
      return;
    }

    initAlmacenes();

    // Sin `?item=` no se rebota a ningún lado: el ítem se elige acá con el select (Sc. 01). Solo
    // hay algo que traer cuando vino preseleccionado.
    if (itemId) {
      initItem();
    }
  }, [itemId]);

  /** El timer del debounce no puede sobrevivir al desmontaje: resolvería contra un componente ido. */
  useEffect(() => () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
  }, []);

  /**
   * Se reusa el select del Pañol2 en vez de exponer uno propio: es el mismo catálogo de depósitos
   * (`almacenes`) y ya devuelve SelectOption{value,label}. Mismo criterio que el resto del módulo
   * con categorías, subcategorías, marcas y unidades.
   */
  const initAlmacenes = () => {
    Utils.getData('/panol2/select/almacenes')
      .then((data) => setAlmacenes(data || []))
      .catch(() => setAlmacenes([]));
  };

  const initItem = () => {
    setLoading(true);
    Utils.getData(URL_CATALOGO + '/' + itemId)
      .then((data) => {
        if (data) {
          setItem(data);
        } else {
          setRedirectTo(URL_CATALOGO);
        }
      })
      .catch(() => setRedirectTo(URL_CATALOGO))
      .finally(() => setLoading(false));
  };

  /**
   * Alimenta el select de ítems. Devuelve la forma que espera react-select v1: {options: [...]}.
   *
   * Las promesas de las llamadas que el debounce descarta quedan sin resolver A PROPÓSITO, y no
   * dejan el spinner colgado: Async ignora todo callback que no sea el de la última llamada
   * ("Ignore all but the most recent request", react-select/lib/Async.js), y la última siempre
   * resuelve.
   */
  const buscarItems = (texto) =>
    new Promise((resolve) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(() => {
        const busqueda = texto && String(texto).trim();
        const query = busqueda ? '?busqueda=' + encodeURIComponent(busqueda) : '';

        Utils.getData(URL_ITEMS_SELECT + query)
          .then((data) => {
            resolve({ options: (data && data.items) || [] });
          })
          .catch(() => {
            resolve({ options: [] });
          });
      }, DEBOUNCE_BUSQUEDA_MS);
    });

  const handleInputChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Los ids de los mensajes van literales y no por variable, para que `formatjs extract` los tome.
  const warn = (message) => {
    Swal.fire({ icon: 'warning', title: message, confirmButtonText: 'OK' });
  };

  const validate = () => {
    // Solo cuando el ítem se elige acá. Si vino por `?item=` es fijo, y que exista ya lo verificó
    // el fetch del efecto.
    if (!itemId && !itemSeleccionado) {
      warn(
        intl.formatMessage({
          id: 'inventarioStock.error.item_required',
          defaultMessage: 'El ítem es requerido',
        })
      );
      return false;
    }

    if (!formData.almacen) {
      warn(
        intl.formatMessage({
          id: 'inventarioStock.error.deposito_required',
          defaultMessage: 'El depósito destino es requerido',
        })
      );
      return false;
    }

    if (formData.cantidad === '' || formData.cantidad === null) {
      warn(
        intl.formatMessage({
          id: 'inventarioStock.error.cantidad_required',
          defaultMessage: 'La cantidad es requerida',
        })
      );
      return false;
    }

    const cantidad = Number(formData.cantidad);

    // Entero, no decimal: una fila de stock es un envase entero. El decimal de la unidad de medida
    // vive en el factor del envase, no acá.
    if (isNaN(cantidad) || cantidad !== Math.floor(cantidad) || cantidad < 1) {
      warn(
        intl.formatMessage({
          id: 'inventarioStock.error.cantidad_invalida',
          defaultMessage: 'La cantidad debe ser un número entero mayor o igual a 1',
        })
      );
      return false;
    }

    if (cantidad > TOPE_CANTIDAD) {
      warn(
        intl.formatMessage(
          {
            id: 'inventarioStock.error.cantidad_supera_tope',
            defaultMessage: 'La cantidad no puede superar {tope} unidades de stock por ingreso.',
          },
          { tope: TOPE_CANTIDAD }
        )
      );
      return false;
    }

    if (formData.costo !== '' && (isNaN(Number(formData.costo)) || Number(formData.costo) < 0)) {
      warn(
        intl.formatMessage({
          id: 'inventarioStock.error.costo_invalido',
          defaultMessage: 'El costo debe ser un número mayor o igual a cero',
        })
      );
      return false;
    }

    if (!formData.fecha) {
      warn(
        intl.formatMessage({
          id: 'inventarioStock.error.fecha_required',
          defaultMessage: 'La fecha es requerida',
        })
      );
      return false;
    }

    return true;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!validate()) return;

    setLoading(true);

    const dataToSend = {
      // El par (tipo, id) viaja completo: la ubicación es polimórfica y un id suelto no identifica
      // nada. Hoy el único tipo del catálogo es 'almacen'.
      ubicacionDestinoType: 'almacen',
      ubicacionDestinoId: Number(formData.almacen),
      item: itemId || (itemSeleccionado ? itemSeleccionado.value : null),
      cantidad: Number(formData.cantidad),
      // Vacío es "ingreso sin precio", que es un valor con significado y no un dato faltante: se
      // manda null y no 0, porque 0 sería un costo real (donación, garantía).
      costo: formData.costo !== '' ? Number(formData.costo) : null,
      fecha: formData.fecha,
      motivo: formData.motivo !== '' ? String(formData.motivo).trim() : null,
    };

    Utils.sendData('/inventario/stock/ingresos', 'POST', dataToSend)
      .then((response) => {
        if (!response) return;

        Swal.fire({
          icon: 'success',
          title: intl.formatMessage({
            id: 'inventarioStock.ingreso.registrado',
            defaultMessage: 'Ingreso registrado',
          }),
          text: intl.formatMessage(
            {
              id: 'inventarioStock.ingreso.envases_ingresados',
              // La oración entera va dentro del bloque `plural` y no solo el sustantivo: acá el
              // verbo también concuerda ("Se ingresó" / "Se ingresaron"), y el traductor necesita
              // la frase completa en cada rama.
              //
              // Dice "unidad de stock" y NO "envase" aunque el modelo llame envase a la fila:
              // `unidades_medida` mezcla empaque (Caja, bidón 10 litros) con escala de unidad
              // (Metro, kilómetro, Unidad), y para un ítem en metros "envase" es falso. Tampoco se
              // puede condicionar la palabra: `factor` no distingue los dos casos — Caja es empaque
              // con factor 1 y kilogramo NO es empaque con factor 1000. Y no se usa la abreviatura
              // del ítem porque es texto libre: daría "Se ingresaron 3 1KM.".
              defaultMessage:
                '{cantidad, plural, one {Se ingresó # unidad de stock.} other {Se ingresaron # unidades de stock.}}',
            },
            { cantidad: response.cantidad }
          ),
          confirmButtonText: 'OK',
        }).then(() => exit());
      })
      // Utils.sendData ya muestra el error por Swal; acá solo se corta la propagación.
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const exit = () => {
    setRedirectTo(urlVolver);
  };

  const descripcionItem = () => {
    if (!item) return '-';

    const partes = [];
    if (item.numeroDeParte) partes.push(item.numeroDeParte);
    if (item.descripcion) partes.push(item.descripcion);

    return partes.length > 0 ? partes.join(' — ') : '-';
  };

  return (
    <React.Fragment>
      {redirectTo && <Redirect push to={redirectTo} />}
      {loading && <Loading />}
      <div className="row">
        <div className="col-md-12">
          <form className="form form-horizontal">
            <div className="form-body">
              <div className="card pull-up">
                <div className="card-content">
                  <div className="card-body">
                    <h4 className="form-section">
                      <i className="icon-home"></i>
                      {intl.formatMessage({
                        id: 'inventarioStock.ingreso.header_datos',
                        defaultMessage: 'Datos del Ingreso',
                      })}
                      <div className="float-right" style={{ fontSize: '14px' }}>
                        *{' '}
                        {intl.formatMessage({
                          id: 'inventarioStock.ingreso.campos_requeridos',
                          defaultMessage: 'campos requeridos',
                        })}
                      </div>
                    </h4>

                    <div className="row">
                      {/* Fijo si vino por `?item=`; select buscable si se entró sin ítem (Sc. 01). */}
                      <div className="col-md-6">
                        <div className="form-group row">
                          <label className="col-md-4 label-control col-form-label" htmlFor="item">
                            {intl.formatMessage({ id: 'inventarioStock.ingreso.item', defaultMessage: 'Ítem' })}
                            {itemId ? ':' : '*:'}
                          </label>
                          <div className="col-md-8">
                            {itemId ? (
                              <div className="form-control-static col-form-label form-value">{descripcionItem()}</div>
                            ) : (
                              /*
                                `cache={false}`: el cache de Async guarda las opciones por texto
                                tipeado y no volvería a consultar, así que volver sobre un texto ya
                                buscado mostraría el catálogo como estaba y no como está. Con el
                                debounce puesto, el ahorro no compensa esa mentira.

                                `filterOptions`: el filtrado ya lo hizo el backend sobre número de
                                parte Y descripción. Volver a filtrar en el cliente contra la
                                etiqueta descartaría un match legítimo el día que la etiqueta
                                cambie de forma.
                              */
                              <Select.Async
                                id="item"
                                name="item"
                                value={itemSeleccionado}
                                loadOptions={buscarItems}
                                cache={false}
                                filterOptions={(options) => options}
                                placeholder={intl.formatMessage({
                                  id: 'inventarioStock.ingreso.item_placeholder',
                                  defaultMessage: 'Buscar por número de parte o descripción...',
                                })}
                                searchPromptText={intl.formatMessage({
                                  id: 'inventarioStock.ingreso.item_prompt',
                                  defaultMessage: 'Escribí para buscar un ítem',
                                })}
                                loadingPlaceholder={intl.formatMessage({
                                  id: 'inventarioStock.ingreso.item_buscando',
                                  defaultMessage: 'Buscando...',
                                })}
                                noResultsText={intl.formatMessage({
                                  id: 'inventarioStock.ingreso.item_sin_resultados',
                                  defaultMessage: 'No hay ítems que coincidan',
                                })}
                                onChange={(opcion) => setItemSeleccionado(opcion)}
                              />
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="form-group row">
                          <label className="col-md-4 label-control col-form-label" htmlFor="almacen">
                            {intl.formatMessage({
                              id: 'inventarioStock.ingreso.deposito_destino',
                              defaultMessage: 'Depósito destino',
                            })}
                            *:
                          </label>
                          <div className="col-md-8">
                            <select
                              className="form-control"
                              id="almacen"
                              name="almacen"
                              value={formData.almacen}
                              onChange={(e) => handleInputChange('almacen', e.target.value)}
                            >
                              <option value="">
                                {intl.formatMessage({ id: 'Seleccione', defaultMessage: 'Seleccione...' })}
                              </option>
                              {almacenes.map((option) => (
                                <option key={'almacen_' + option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group row">
                          <label className="col-md-4 label-control col-form-label" htmlFor="cantidad">
                            {intl.formatMessage({
                              id: 'inventarioStock.ingreso.cantidad',
                              defaultMessage: 'Cantidad',
                            })}
                            *:
                          </label>
                          <div className="col-md-8">
                            {/* step 1: la cantidad es de envases enteros, sin decimales. */}
                            <input
                              type="number"
                              min="1"
                              max={TOPE_CANTIDAD}
                              step="1"
                              className="form-control"
                              id="cantidad"
                              name="cantidad"
                              value={formData.cantidad}
                              onChange={(e) => handleInputChange('cantidad', e.target.value)}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="form-group row">
                          <label className="col-md-4 label-control col-form-label" htmlFor="costo">
                            {intl.formatMessage({
                              id: 'inventarioStock.ingreso.costo_unitario',
                              defaultMessage: 'Costo unitario',
                            })}
                            :
                          </label>
                          <div className="col-md-8">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              className="form-control"
                              id="costo"
                              name="costo"
                              value={formData.costo}
                              onChange={(e) => handleInputChange('costo', e.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group row">
                          <label className="col-md-4 label-control col-form-label" htmlFor="fecha">
                            {intl.formatMessage({ id: 'inventarioStock.ingreso.fecha', defaultMessage: 'Fecha' })}*:
                          </label>
                          <div className="col-md-8">
                            <input
                              type="date"
                              className="form-control"
                              id="fecha"
                              name="fecha"
                              value={formData.fecha}
                              onChange={(e) => handleInputChange('fecha', e.target.value)}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="form-group row">
                          <label className="col-md-4 label-control col-form-label" htmlFor="motivo">
                            {intl.formatMessage({ id: 'inventarioStock.ingreso.motivo', defaultMessage: 'Motivo' })}:
                          </label>
                          <div className="col-md-8">
                            <input
                              type="text"
                              maxLength="255"
                              className="form-control"
                              id="motivo"
                              name="motivo"
                              value={formData.motivo}
                              onChange={(e) => handleInputChange('motivo', e.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-12">
                        <small className="text-muted">
                          <FormattedMessage
                            id="inventarioStock.ingreso.ayuda_costo"
                            defaultMessage="Costo unitario: precio de cada unidad de stock. Dejarlo vacío registra el ingreso sin precio."
                          />
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card pull-up">
                <div className="card-content">
                  <div className="card-body">
                    <div className="text-cd text-right">
                      <button onClick={handleSubmit} className="btn btn-primary mr-1">
                        <i className="fa fa-check-circle"></i>{' '}
                        {intl.formatMessage({ id: 'inventarioStock.ingreso.guardar', defaultMessage: 'Guardar' })}
                      </button>
                      <button type="button" className="btn btn-danger" onClick={() => exit()}>
                        <i className="fa fa-times-circle"></i>{' '}
                        {intl.formatMessage({ id: 'inventarioStock.ingreso.cancelar', defaultMessage: 'Cancelar' })}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </React.Fragment>
  );
};

export default InventarioStockIngresoAbm;
